import { DragAndDrop } from '@eds/vanilla';
import { useEffect, useRef } from 'react';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';

interface fileState {
  file: File;
  id: number;
  state: string;
}

interface Props {
  onFileSelected: (file: File) => void;
  onFileUnselected: () => void;
}

function FileUploader(props: Props) {
  const { addNotifications } = useSwobNotifications();

  const fileTimeoutMap: { [key in string]: NodeJS.Timeout | undefined } = {};

  // let dragAndDrop: DragAndDrop;
  const dragAndDropRef = useRef<DragAndDrop | null>(null);

  useEffect(() => {
    const dadEl = document.querySelector('#TestCollectionDragAndDrop') as HTMLElement;
    if (dadEl && !dragAndDropRef.current) {
      dragAndDropRef.current = new DragAndDrop({
        element: dadEl,
        onAbort: onAbort,
        onBeforeSelect: onBeforeSelect,
        onSelect: onSelect,
        onStateChange: onStateChange
      });
      dragAndDropRef.current.init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openE2E = (title: string, message: string, color: string, icon: string) => {
    addNotifications([
      {
        title: title,
        icon: icon,
        description: message,
        timestamp: new Date(),
        color: color
      }
    ]);
  };

  const typeValidation = (type: string): boolean => {
    //for later type !== '.json' && type !== '.tar' && type !== '.yaml' && type !== '.yml'
    return type === '.tar' || type === '.zip';
  };

  const onBeforeSelect = (_file: File, state: fileState[]) => {
    if (state.length < 1) {
      const type = _file.name.substring(_file.name.lastIndexOf('.'));
      if (typeValidation(type)) {
        return true;
      }
      openE2E(`Unsupported file type`, 'Unsupported file format. Please upload a valid TAR, ZIP file.', 'red', 'icon-triangle-warning');
      return false;
    } else {
      openE2E(`File not uploaded`, 'You can only upload 1 file', 'red', 'icon-triangle-warning');
      return false;
    }
  };

  const onSelect = (id: number, _file: File, next: (progress: number) => void, complete: () => void, error: () => void) => {
    let progress = 0;
    const speed = Math.random() * 5 + 1;

    const interval = setInterval(() => {
      progress += speed;
      if (progress >= 100) {
        complete();
        fileTimeoutMap[id] = undefined;
        clearInterval(interval);
      } else if (Math.random() * 1000 > 980) {
        error();
        fileTimeoutMap[id] = undefined;
        clearInterval(interval);
      } else {
        next(progress);
      }
    }, 500);
    fileTimeoutMap[id] = interval;
  };

  const onAbort = (id: number) => {
    if (fileTimeoutMap[id] !== undefined) {
      clearInterval(fileTimeoutMap[id]);
      fileTimeoutMap[id] = undefined;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onStateChange = (state: fileState, _states: fileState[]) => {
    if (state.state === 'completed') {
      props.onFileSelected(state.file);
    } else if (state.state === 'error') {
      openE2E(
        'Unexpected error occurred',
        `Failed to upload test collection file. Please check your input and try again`,
        'red',
        'icon-triangle-warning'
      );
    } else if (state.state === 'removed') {
      props.onFileUnselected();
    }
  };

  return (
    <div id="TestCollectionDragAndDrop" className="drag-and-drop">
      <form className="drag-and-drop-area">
        <i className="icon icon-upload"></i>
        <p>
          Drag & drop your file here or <br />
          <label htmlFor="file-upload" role="button" aria-label="Select files to upload">
            select to browse{' '}
            <input className="files" id="file-upload" type="file" multiple accept=".tar,.zip" /> 
          </label>
        </p>
        <p>
          Supported formats: .tar, .zip
          <br />
          Max: 1 file
        </p>
      </form>

      <div className="header header-files hidden">
        <div className="left">Uploaded file</div>
      </div>
      <div className="content content-files hidden">
        <div className="file-pills"></div>
        <table className="upload-table"></table>
      </div>
    </div>
  );
}

export default FileUploader;
